import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaPen } from 'react-icons/fa';
import NavBar from './NavBar.js';
import Footer from './Footer.js';
import '../styles/About.css';
import { useAboutContent, EMPTY_ABOUT_CONTENT } from '../../ViewModel/AboutViewModel.js';

function About({ adminMode = false, onAdminLogout = () => {}, isAdminLoggedIn = false }) {
  const {
    content: remoteContent,
    loading,
    error,
    saveContent,
    uploadPortraitImage,
    deletePortraitImageByUrl,
  } = useAboutContent();
  const [content, setContent] = useState(EMPTY_ABOUT_CONTENT);
  const [editing, setEditing] = useState({
    hero: false,
    portrait: false,
    stats: false,
    timeline: false,
    posts: false,
    cta: false,
  });
  const [saveStatus, setSaveStatus] = useState(null);
  const [activeEditor, setActiveEditor] = useState(null);
  const [expandedTimeline, setExpandedTimeline] = useState({});
  const [expandedPosts, setExpandedPosts] = useState({});
  const [isHeroExpanded, setIsHeroExpanded] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 640 : false));
  const [isBlogExpanded, setIsBlogExpanded] = useState(false);
  const [portraitUploading, setPortraitUploading] = useState(false);
  const portraitSrcRef = useRef('');
  const editorRefs = useRef({
    hero: null,
    portrait: null,
    stats: null,
    timeline: null,
    posts: null,
    cta: null,
  });

  const normalizeContent = (data) => {
    // Ensure all arrays/objects exist to avoid undefined.map errors
    const hero = data.hero || {};
    const portrait = data.portrait || {};
    return {
      hero: {
        eyebrow: hero.eyebrow || '',
        title: hero.title || '',
        lead: hero.lead || '',
        meta: Array.isArray(hero.meta) ? hero.meta : [],
      },
      portrait: {
        chip: portrait.chip || '',
        quote: portrait.quote || '',
        src: portrait.src || '',
        showImage: portrait.showImage !== false,
      },
      stats: Array.isArray(data.stats) ? data.stats : [],
      timeline: Array.isArray(data.timeline) ? data.timeline : [],
      posts: Array.isArray(data.posts) ? data.posts : [],
      postsButton: data.postsButton || '',
      cta: {
        eyebrow: (data.cta && data.cta.eyebrow) || '',
        heading: (data.cta && data.cta.heading) || '',
        button: (data.cta && data.cta.button) || '',
      },
    };
  };

  const getStorageObjectPathFromUrl = (imageUrl) => {
    if (!imageUrl) return null;
    const marker = '/images/';
    const markerIndex = imageUrl.indexOf(marker);
    if (markerIndex === -1) return null;
    return decodeURIComponent(imageUrl.slice(markerIndex + marker.length));
  };

  const isAboutStorageUrl = (imageUrl) => {
    const objectPath = getStorageObjectPathFromUrl(imageUrl);
    return Boolean(objectPath && objectPath.startsWith('about/'));
  };

  useEffect(() => {
    if (remoteContent) {
      setContent(normalizeContent(remoteContent));
    } else if (!loading && !remoteContent) {
      setContent(EMPTY_ABOUT_CONTENT);
    }
  }, [remoteContent, loading]);

  useEffect(() => {
    portraitSrcRef.current = (content?.portrait?.src || '').trim();
  }, [content?.portrait?.src]);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      setIsMobileView(window.innerWidth <= 640);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updateField = (path, value) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      // enkel path updater
      const keys = path.split('.');
      let ref = next;
      keys.slice(0, -1).forEach((k) => {
        ref = ref[k];
      });
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateListItem = (listName, index, field, value) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      next[listName][index][field] = value;
      return next;
    });
  };

  const addListItem = (listName, blankItem) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      next[listName].push(blankItem);
      return next;
    });
  };

  const deleteListItem = (listName, index) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      next[listName].splice(index, 1);
      return next;
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || portraitUploading) return;

    const currentPortraitSrc = portraitSrcRef.current;

    setPortraitUploading(true);
    try {
      if (currentPortraitSrc && isAboutStorageUrl(currentPortraitSrc)) {
        await deletePortraitImageByUrl(currentPortraitSrc);
      }

      const uploadResult = await uploadPortraitImage(file);
      portraitSrcRef.current = uploadResult.publicUrl;
      updateField('portrait.src', uploadResult.publicUrl);
      setSaveStatus(`Bild uppladdad${uploadResult.format ? ` (${uploadResult.format})` : ''}. Klicka Spara för att publicera.`);
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      setSaveStatus(`Fel vid uppladdning: ${err.message}`);
      setTimeout(() => setSaveStatus(null), 2200);
    } finally {
      setPortraitUploading(false);
      e.target.value = '';
    }
  };

  const resetContent = () => setContent(remoteContent ?? EMPTY_ABOUT_CONTENT);

  const closeEditor = (key) => {
    setEditing((prev) => ({ ...prev, [key]: false }));
    setActiveEditor(null);
  };

  const cancelSection = async (key) => {
    const source = normalizeContent(remoteContent ?? EMPTY_ABOUT_CONTENT);

    if (key === 'portrait') {
      const currentPortraitSrc = (content?.portrait?.src || '').trim();
      const remotePortraitSrc = (source.portrait?.src || '').trim();

      if (
        currentPortraitSrc &&
        currentPortraitSrc !== remotePortraitSrc &&
        isAboutStorageUrl(currentPortraitSrc)
      ) {
        try {
          await deletePortraitImageByUrl(currentPortraitSrc);
        } catch (_) {
        }
      }
    }

    setContent((prev) => {
      const next = structuredClone(prev);

      if (key === 'hero') next.hero = structuredClone(source.hero);
      if (key === 'portrait') next.portrait = structuredClone(source.portrait);
      if (key === 'stats') next.stats = structuredClone(source.stats);
      if (key === 'timeline') next.timeline = structuredClone(source.timeline);
      if (key === 'posts') {
        next.posts = structuredClone(source.posts);
        next.postsButton = source.postsButton;
      }
      if (key === 'cta') next.cta = structuredClone(source.cta);

      return next;
    });

    closeEditor(key);
  };

  const saveSection = async (key, label) => {
    try {
      const previousPortraitSrc = key === 'portrait'
        ? (normalizeContent(remoteContent ?? EMPTY_ABOUT_CONTENT).portrait.src || '').trim()
        : '';
      const nextPortraitSrc = (content?.portrait?.src || '').trim();

      await saveContent(content);

      if (
        key === 'portrait' &&
        previousPortraitSrc &&
        nextPortraitSrc &&
        previousPortraitSrc !== nextPortraitSrc
      ) {
        try {
          await deletePortraitImageByUrl(previousPortraitSrc);
        } catch (deleteErr) {
          setSaveStatus(`Porträtt sparad, men kunde inte radera tidigare bild: ${deleteErr.message}`);
          closeEditor(key);
          setTimeout(() => setSaveStatus(null), 2200);
          return;
        }
      }

      setSaveStatus(`${label} sparad!`);
      closeEditor(key);
    } catch (err) {
      setSaveStatus('Fel vid sparande');
    } finally {
      setTimeout(() => setSaveStatus(null), 1600);
    }
  };

  const openEditor = (key) => {
    setEditing((prev) => ({ ...prev, [key]: true }));
    setActiveEditor(key);

    setTimeout(() => {
      const section = editorRefs.current[key];
      if (!section) return;

      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstField = section.querySelector('input, textarea, button');
      if (firstField && typeof firstField.focus === 'function') {
        firstField.focus({ preventScroll: true });
      }
    }, 80);
  };

  useEffect(() => {
    if (!activeEditor) return undefined;

    const timeoutId = setTimeout(() => {
      setActiveEditor(null);
    }, 1800);

    return () => clearTimeout(timeoutId);
  }, [activeEditor]);

  const handleSave = async () => {
    try {
      await saveContent(content);
      setSaveStatus('Sparat!');
    } catch (err) {
      setSaveStatus('Fel vid sparande');
    } finally {
      setTimeout(() => setSaveStatus(null), 1600);
    }
  };

  // Extra guards so render never crashes if content shape is partial
  const safeHeroMeta = Array.isArray(content?.hero?.meta) ? content.hero.meta : [];
  const safeStats = Array.isArray(content?.stats) ? content.stats : [];
  const safePosts = Array.isArray(content?.posts) ? content.posts : [];
  const safeTimeline = useMemo(
    () => (Array.isArray(content?.timeline) ? content.timeline : []),
    [content?.timeline]
  );

  const timelineWithStep = useMemo(
    () => safeTimeline.map((step, idx) => ({ ...step, step: idx + 1 })),
    [safeTimeline]
  );
  const portraitSrc = (content?.portrait?.src || '').trim();

  const toggleTimelineExpand = (index) => {
    setExpandedTimeline((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const togglePostExpand = (index) => {
    setExpandedPosts((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const toggleBlogSection = () => {
    setIsBlogExpanded((prev) => {
      const next = !prev;
      if (!next) {
        setExpandedPosts({});
        setEditing((current) => ({ ...current, posts: false }));
        setActiveEditor((current) => (current === 'posts' ? null : current));
      }
      return next;
    });
  };

  const portraitChip = (content?.portrait?.chip || '').trim();
  const showPortraitImage = content?.portrait?.showImage !== false;
  const shouldCollapseLead = isMobileView && !isHeroExpanded;
  const showTimelineOnAbout = false; // Toggle to true if timeline should be shown again on About page.

  if (loading && !remoteContent) {
    return (
      <div className={`about-page ${adminMode ? 'admin-active' : ''}`}>
        {!adminMode && (
          <NavBar adminMode={adminMode} onAdminLogout={onAdminLogout} isAdminLoggedIn={isAdminLoggedIn} />
        )}
        <p style={{ padding: '24px' }}>Laddar innehåll...</p>
        <Footer />
      </div>
    );
  }

  if (error && !remoteContent) {
    return (
      <div className={`about-page ${adminMode ? 'admin-active' : ''}`}>
        {!adminMode && (
          <NavBar adminMode={adminMode} onAdminLogout={onAdminLogout} isAdminLoggedIn={isAdminLoggedIn} />
        )}
        <p style={{ padding: '24px', color: 'red' }}>Fel: {error}</p>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`about-page ${adminMode ? 'admin-active' : ''}`}
      style={{ '--about-hero-bg-image': "url('/Images/works.jpg')" }}
    >
      <NavBar adminMode={adminMode} onAdminLogout={onAdminLogout} isAdminLoggedIn={isAdminLoggedIn} />
      <main className="about-main">

        {adminMode && (
          <button className="save-fab" onClick={handleSave}>
            Spara ändringar
          </button>
        )}

        {adminMode && saveStatus && (
          <div className="save-toast">{saveStatus}</div>
        )}

        {adminMode && activeEditor && (
          <div className="admin-edit-status">Redigerar nu: {activeEditor}</div>
        )}

        <section className="about-hero section-editable">
          <div className="about-hero-text">
            {adminMode && (
              <div className="edit-dock hero-dock">
                <button className="about-edit-pen" onClick={() => openEditor('hero')}>
                  <FaPen size={14} /> Redigera hero
                </button>
                <button className="about-edit-pen ghost" onClick={resetContent}>Ladda om</button>
              </div>
            )}
            <h1>{content.hero.title}</h1>
            <p className={`lead ${shouldCollapseLead ? 'lead-collapsed' : ''}`}>{content.hero.lead}</p>
            {isMobileView && content.hero.lead?.length > 0 && (
              <button
                type="button"
                className="lead-toggle"
                onClick={() => setIsHeroExpanded((prev) => !prev)}
              >
                {isHeroExpanded ? 'Visa mindre' : 'Läs mer'}
              </button>
            )}
            <div className="about-hero-meta">
              {safeHeroMeta.map((item, idx) => (
                <div key={idx}>
                  <span className="meta-label">{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
            {adminMode && editing.hero && (
              <div
                className={`inline-edit ${activeEditor === 'hero' ? 'inline-edit--active' : ''}`}
                ref={(node) => {
                  editorRefs.current.hero = node;
                }}
              >
                <label>
                  Eyebrow
                  <input name="hero-eyebrow" value={content.hero.eyebrow} onChange={(e) => updateField('hero.eyebrow', e.target.value)} />
                </label>
                <label>
                  Rubrik
                  <input name="hero-title" value={content.hero.title} onChange={(e) => updateField('hero.title', e.target.value)} />
                </label>
                <label>
                  Introtext
                  <textarea name="hero-lead" value={content.hero.lead} onChange={(e) => updateField('hero.lead', e.target.value)} />
                </label>
                <div className="inline-edit-grid">
                  {safeHeroMeta.map((item, idx) => (
                    <div key={idx} className="inline-edit-stack">
                      <input
                        name={`hero-meta-label-${idx}`}
                        value={item.label}
                        onChange={(e) => updateListItem('hero.meta', idx, 'label', e.target.value)}
                        placeholder="Label"
                      />
                      <input
                        name={`hero-meta-value-${idx}`}
                        value={item.value}
                        onChange={(e) => updateListItem('hero.meta', idx, 'value', e.target.value)}
                        placeholder="Värde"
                      />
                    </div>
                  ))}
                </div>
                <div className="inline-edit-actions">
                  <button className="about-edit-pen" type="button" onClick={() => saveSection('hero', 'Hero')}>
                    Spara
                  </button>
                  <button className="about-edit-pen ghost" type="button" onClick={() => cancelSection('hero')}>
                    Avbryt
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="about-hero-card card-editable">
            {adminMode && (
              <div className="edit-dock">
                <button className="about-edit-pen" onClick={() => openEditor('portrait')}>
                  <FaPen size={14} /> Porträtt
                </button>
                <label className="about-edit-pen ghost">
                  {portraitUploading ? 'Laddar upp...' : 'Byt bild'}
                  <input
                    name="portrait-image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={portraitUploading}
                  />
                </label>
              </div>
            )}
            {portraitChip && <span className="chip card-chip">{portraitChip}</span>}
            {showPortraitImage && portraitSrc ? (
              <div className="portrait">
                <img
                  src={portraitSrc}
                  alt="Porträtt av Amina"
                />
              </div>
            ) : null}
            {adminMode && (
              <div className="stats-under-image">
                <button className="about-edit-pen ghost" onClick={() => openEditor('stats')}>
                  <FaPen size={14} /> Siffror
                </button>
              </div>
            )}
            <div className="quote">{content.portrait.quote}</div>

            <div className="stats-block">
              <div className="card-grid">
                {safeStats.map((stat, idx) => (
                  <div className="stat" key={idx}>
                    <p>{stat.label}</p>
                    <h3>{stat.value}</h3>
                  </div>
                ))}
              </div>

              {adminMode && editing.stats && (
                <div
                  className={`inline-edit ${activeEditor === 'stats' ? 'inline-edit--active' : ''}`}
                  ref={(node) => {
                    editorRefs.current.stats = node;
                  }}
                >
                  <div className="inline-edit-grid">
                    {safeStats.map((stat, idx) => (
                      <div key={idx} className="inline-edit-stack">
                        <input
                          name={`stats-label-${idx}`}
                          value={stat.label}
                          onChange={(e) => updateListItem('stats', idx, 'label', e.target.value)}
                          placeholder="Label"
                        />
                        <input
                          name={`stats-value-${idx}`}
                          value={stat.value}
                          onChange={(e) => updateListItem('stats', idx, 'value', e.target.value)}
                          placeholder="Värde"
                        />
                        <button
                          className="about-edit-pen ghost danger"
                          onClick={() => deleteListItem('stats', idx)}
                          type="button"
                        >
                          Ta bort
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    className="about-edit-pen ghost"
                    onClick={() => addListItem('stats', { label: 'Ny siffra', value: '0' })}
                    type="button"
                  >
                    Lägg till siffra
                  </button>
                  <div className="inline-edit-actions">
                    <button className="about-edit-pen" type="button" onClick={() => saveSection('stats', 'Siffror')}>
                      Spara
                    </button>
                    <button className="about-edit-pen ghost" type="button" onClick={() => cancelSection('stats')}>
                      Avbryt
                    </button>
                  </div>
                </div>
              )}
            </div>

            {adminMode && editing.portrait && (
              <div
                className={`inline-edit ${activeEditor === 'portrait' ? 'inline-edit--active' : ''}`}
                ref={(node) => {
                  editorRefs.current.portrait = node;
                }}
              >
                <label>
                  Chiptext
                  <input name="portrait-chip" value={content.portrait.chip} onChange={(e) => updateField('portrait.chip', e.target.value)} />
                </label>
                <label className="checkbox-field">
                  <input
                    type="checkbox"
                    name="portrait-show"
                    checked={showPortraitImage}
                    onChange={(e) => updateField('portrait.showImage', e.target.checked)}
                  />
                  Visa porträttbild
                </label>
                <label>
                  Citat
                  <textarea name="portrait-quote" value={content.portrait.quote} onChange={(e) => updateField('portrait.quote', e.target.value)} />
                </label>
                <div className="inline-edit-actions">
                  <button className="about-edit-pen" type="button" onClick={() => saveSection('portrait', 'Porträtt')}>
                    Spara
                  </button>
                  <button className="about-edit-pen ghost" type="button" onClick={() => cancelSection('portrait')}>
                    Avbryt
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

      {showTimelineOnAbout && (
      <section className="about-timeline section-editable">
        <div className="section-header about-timeline-header">
          <p className="eyebrow">Resan</p>
          <div className="section-header_row">
            <h2>Så här jobbar vi</h2>
            {adminMode && (
              <div className="edit-dock edit-dock--inline">
                <button className="about-edit-pen" onClick={() => openEditor('timeline')}>
                  <FaPen size={14} /> Redigera tidslinje
                </button>
              </div>
            )}
          </div>
          <p className="muted">Från första mötet till löpande samarbete</p>
        </div>
        <div className="timeline">
          {timelineWithStep.map((step, idx) => (
            <div className="timeline-card" key={`${step.title}-${idx}`}>
              {(() => {
                const isExpanded = Boolean(expandedTimeline[idx]);

                return (
                  <>
              <div className="step">0{step.step}</div>
              <h3>{step.title}</h3>
              <p className={`timeline-body ${!isExpanded ? 'timeline-body--collapsed' : ''}`}>{step.body}</p>
              <button
                className="timeline-read-more"
                type="button"
                onClick={() => toggleTimelineExpand(idx)}
                aria-label={isExpanded ? 'Dölj text' : 'Visa text'}
              >
                {isExpanded ? 'Visa mindre' : 'Läs mer'}
              </button>
                  </>
                );
              })()}
            </div>
          ))}
        </div>
        {adminMode && editing.timeline && (
          <div
            className={`inline-edit ${activeEditor === 'timeline' ? 'inline-edit--active' : ''}`}
            ref={(node) => {
              editorRefs.current.timeline = node;
            }}
          >
            {safeTimeline.map((step, idx) => (
              <div key={idx} className="inline-edit-stack timeline-edit-row">
                <input
                  name={`timeline-title-${idx}`}
                  value={step.title}
                  onChange={(e) => updateListItem('timeline', idx, 'title', e.target.value)}
                  placeholder="Titel"
                />
                <textarea
                  name={`timeline-body-${idx}`}
                  value={step.body}
                  onChange={(e) => updateListItem('timeline', idx, 'body', e.target.value)}
                  placeholder="Brödtext"
                />
                <button
                  className="about-edit-pen ghost danger"
                  onClick={() => deleteListItem('timeline', idx)}
                  type="button"
                >
                  Ta bort
                </button>
              </div>
            ))}
            <button
              className="about-edit-pen ghost"
              onClick={() => addListItem('timeline', { title: 'Ny fas', body: 'Beskrivning' })}
              type="button"
            >
              Lägg till fas
            </button>
            <div className="inline-edit-actions">
              <button className="about-edit-pen" type="button" onClick={() => saveSection('timeline', 'Tidslinje')}>
                Spara
              </button>
              <button className="about-edit-pen ghost" type="button" onClick={() => cancelSection('timeline')}>
                Avbryt
              </button>
            </div>
          </div>
        )}
      </section>
      )}

      <section className="about-blog section-editable">
        <div className="section-header about-blog-header">
          <p className="eyebrow">Insikter</p>
          <div className="section-header_row">
            <h2>Anteckningar som redovisningskonsult</h2>
            {adminMode && (
              <div className="edit-dock edit-dock--inline">
                <button
                  className="about-edit-pen"
                  onClick={() => {
                    setIsBlogExpanded(true);
                    openEditor('posts');
                  }}
                >
                  <FaPen size={14} /> Redigera bloggkort
                </button>
              </div>
            )}
          </div>
          <p className="muted">Praktiska tips, vanliga fallgropar och hur du slipper dem.</p>
        </div>

        <div className="blog-toggle-wrap">
          <button
            className="blog-toggle-btn"
            type="button"
            aria-label={isBlogExpanded ? 'Dölj bloggkort' : 'Visa bloggkort'}
            onClick={toggleBlogSection}
          >
            {isBlogExpanded ? '−' : '+'}
          </button>
        </div>

        {isBlogExpanded && (
          <div className="blog-grid">
            {safePosts.map((post, index) => (
              <article
                className="blog-card"
                key={`${post.title}-${index}`}
                role="button"
                tabIndex={0}
                onClick={() => togglePostExpand(index)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    togglePostExpand(index);
                  }
                }}
                aria-label={`Visa eller dölj artikel: ${post.title}`}
              >
                {(() => {
                  const isExpanded = Boolean(expandedPosts[index]);

                  return (
                    <>
                <span className="tag">{post.tag}</span>
                <h3>{post.title}</h3>
                <p className={`blog-body ${!isExpanded ? 'blog-body--collapsed' : ''}`}>{post.body}</p>
                <button
                  className="blog-read-more"
                  onClick={(event) => {
                    event.stopPropagation();
                    togglePostExpand(index);
                  }}
                  type="button"
                  aria-label={isExpanded ? 'Dölj text' : 'Visa text'}
                >
                  {isExpanded ? '−' : '+'}
                </button>
                    </>
                  );
                })()}
              </article>
            ))}
          </div>
        )}
        {adminMode && editing.posts && (
          <div
            className={`inline-edit ${activeEditor === 'posts' ? 'inline-edit--active' : ''}`}
            ref={(node) => {
              editorRefs.current.posts = node;
            }}
          >
            {safePosts.map((post, idx) => (
              <div key={idx} className="inline-edit-stack">
                <input
                  name={`posts-tag-${idx}`}
                  value={post.tag}
                  onChange={(e) => updateListItem('posts', idx, 'tag', e.target.value)}
                  placeholder="Tagg"
                />
                <input
                  name={`posts-title-${idx}`}
                  value={post.title}
                  onChange={(e) => updateListItem('posts', idx, 'title', e.target.value)}
                  placeholder="Titel"
                />
                <textarea
                  name={`posts-body-${idx}`}
                  value={post.body}
                  onChange={(e) => updateListItem('posts', idx, 'body', e.target.value)}
                  placeholder="Brödtext"
                />
                <button
                  className="about-edit-pen ghost danger"
                  onClick={() => deleteListItem('posts', idx)}
                  type="button"
                >
                  Ta bort
                </button>
              </div>
            ))}
            <label>
              Knapptext
              <input
                name="posts-button"
                value={content.postsButton}
                onChange={(e) => updateField('postsButton', e.target.value)}
                placeholder="Knapptext"
              />
            </label>
            <button
              className="about-edit-pen ghost"
              onClick={() => addListItem('posts', { title: 'Ny artikel', tag: 'Tagg', body: 'Brödtext' })}
              type="button"
            >
              Lägg till artikel
            </button>
            <div className="inline-edit-actions">
              <button className="about-edit-pen" type="button" onClick={() => saveSection('posts', 'Bloggkort')}>
                Spara
              </button>
              <button className="about-edit-pen ghost" type="button" onClick={() => cancelSection('posts')}>
                Avbryt
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="about-cta section-editable">
        <div>
          <p className="eyebrow">{content.cta.eyebrow}</p>
          <h2>{content.cta.heading}</h2>
        </div>
        {adminMode && (
          <div className="edit-dock">
            <button className="about-edit-pen" onClick={() => openEditor('cta')}>
              <FaPen size={14} /> Redigera CTA
            </button>
          </div>
        )}
        <button className="cta-btn" onClick={() => window.location.href = '/kontakt'}>{content.cta.button}</button>
        {adminMode && editing.cta && (
          <div
            className={`inline-edit ${activeEditor === 'cta' ? 'inline-edit--active' : ''}`}
            ref={(node) => {
              editorRefs.current.cta = node;
            }}
          >
            <label>
              Eyebrow
              <input name="cta-eyebrow" value={content.cta.eyebrow} onChange={(e) => updateField('cta.eyebrow', e.target.value)} />
            </label>
            <label>
              Rubrik
              <input name="cta-heading" value={content.cta.heading} onChange={(e) => updateField('cta.heading', e.target.value)} />
            </label>
            <label>
              Knapptext
              <input name="cta-button" value={content.cta.button} onChange={(e) => updateField('cta.button', e.target.value)} />
            </label>
            <div className="inline-edit-actions">
              <button className="about-edit-pen" type="button" onClick={() => saveSection('cta', 'CTA')}>
                Spara
              </button>
              <button className="about-edit-pen ghost" type="button" onClick={() => cancelSection('cta')}>
                Avbryt
              </button>
            </div>
          </div>
        )}
      </section>
      </main>

      <Footer />
    </div>
  );
}

export default About;
